import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../auth/AuthProvider";
import { pendingOperationalCommands, submitOperationalCommand } from "./operational-command";
import { erpApi, useDevelopmentFixtures } from "../api/client";
import { useLiveSync, type LiveConnection } from "../hooks/useLiveSync";
import type { AppSnapshot, CommandRequest, DataStatus } from "./types";

interface DataContextValue {
  pendingCommands: Array<Omit<CommandRequest,"commandId">>;
  snapshot: AppSnapshot | null;
  status: DataStatus;
  error: string | null;
  connection: LiveConnection;
  refresh: () => Promise<void>;
  executeCommand: (command: Omit<CommandRequest, "commandId">) => Promise<void>;
}

const DataContext = createContext<DataContextValue | null>(null);

async function getDevelopmentSnapshot(): Promise<AppSnapshot> {
  const { cloneFixtureSnapshot } = await import("../dev/fixtures");
  await new Promise((resolve) => window.setTimeout(resolve, 360));
  return cloneFixtureSnapshot();
}

function createCommandId(): string {
  return crypto.randomUUID();
}

export function DataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [pendingCommands, setPendingCommands] = useState(() => pendingOperationalCommands(user?.id ?? "anonymous"));
  const [snapshot, setSnapshot] = useState<AppSnapshot | null>(null);
  const [status, setStatus] = useState<DataStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setStatus((current) => (current === "ready" ? current : "loading"));
      const next = useDevelopmentFixtures ? await getDevelopmentSnapshot() : await erpApi.snapshot();
      setSnapshot(next);
      setStatus(next.orders.length || next.bookings.length ? "ready" : "empty");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "No pudimos actualizar la información.");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const connection = useLiveSync({ onRefresh: refresh });

  const applyDevelopmentCommand = useCallback((command: CommandRequest) => {
    setSnapshot((current) => {
      if (!current) return current;
      const next = structuredClone(current);
      if (command.type === "booking.mark_attended") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          booking.attended = true;
          booking.status = "completed";
          booking.version += 1;
        }
      }
      if (command.type === "booking.assign_agent") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          const member = next.team.find((item) => item.agentSourceId === command.payload.agent_source_id);
          booking.assignedTo = member?.name ?? "Sin asignar";
          if (member?.agentSourceId) booking.assignedAgentSourceId = member.agentSourceId;
          else delete booking.assignedAgentSourceId;
          booking.version += 1;
        }
      }
      if (command.type === "booking.add_note") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          booking.notes.unshift({
            id: command.commandId,
            body: String(command.payload.note ?? ""),
            author: "Usuario actual",
            createdAt: new Date().toISOString(),
          });
          booking.version += 1;
        }
      }
      if (command.type === "booking.set_language") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking && (command.payload.language === "es" || command.payload.language === "en")) {
          booking.language = command.payload.language;
          booking.version += 1;
          for (const confirmation of next.confirmations.filter((item) => item.bookingReference === booking.reference)) {
            confirmation.language = booking.language;
          }
        }
      }
      if (command.type === "booking.update_details") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          booking.customer = String(command.payload.customer_name ?? booking.customer);
          booking.email = String(command.payload.customer_email ?? booking.email);
          booking.phone = String(command.payload.customer_phone ?? booking.phone);
          if (command.payload.language === "es" || command.payload.language === "en") booking.language = command.payload.language;
          booking.version += 1;
        }
      }
      if (command.type === "booking.reschedule") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking && typeof command.payload.date === "string") {
          booking.date = command.payload.date;
          if (typeof command.payload.time === "string") booking.time = command.payload.time;
          booking.version += 1;
        }
      }
      if (command.type === "booking.change_group") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking && typeof command.payload.students === "number") {
          booking.guests = command.payload.students;
          booking.version += 1;
        }
      }
      if (command.type === "booking.add_paid_addon") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          const quantity = Number(command.payload.quantity ?? 1);
          const unitPrice = Number(command.payload.unit_price ?? 0);
          booking.addons.push({ key: command.commandId, name: String(command.payload.name ?? "Producto manual"), quantity, unitPrice, subtotal: quantity * unitPrice, perPerson: false, currency: "MXN" });
          booking.addonsTotal = (booking.addonsTotal ?? 0) + quantity * unitPrice;
          booking.total = (booking.total ?? 0) + quantity * unitPrice;
          booking.version += 1;
        }
      }
      if (command.type === "booking.send_confirmation") {
        const booking = next.bookings.find((item) => item.id === command.aggregateId);
        if (booking) {
          const confirmation = next.confirmations.find((item) => item.bookingReference === booking.reference);
          if (confirmation) {
            confirmation.smtpStatus = "accepted";
            confirmation.recipient = booking.email;
            confirmation.documentAvailable = true;
            confirmation.attempts += 1;
            confirmation.lastAttemptAt = new Date().toISOString();
          }
        }
      }
      if (command.type === "booking.delete_permanently") {
        next.bookings = next.bookings.filter((item) => item.id !== command.aggregateId);
      }
      if (command.type === "lead.mark_followup") {
        const lead = next.leads.find((item) => item.id === command.aggregateId);
        if (lead) {
          lead.status = "contacted";
          lead.version += 1;
        }
      }
      if (command.type === "lead.close") {
        const lead = next.leads.find((item) => item.id === command.aggregateId);
        if (lead) {
          lead.status = "lost";
          lead.version += 1;
        }
      }
      if (command.type === "confirmation.resend") {
        const confirmation = next.confirmations.find((item) => item.id === command.aggregateId);
        if (confirmation) {
          confirmation.smtpStatus = "resend_requested";
          confirmation.attempts += 1;
          confirmation.version += 1;
          confirmation.lastAttemptAt = new Date().toISOString();
        }
      }
      next.generatedAt = new Date().toISOString();
      return next;
    });
  }, []);

  const executeCommand = useCallback(
    async (request: Omit<CommandRequest, "commandId">) => {
      const command: CommandRequest = { ...request, commandId: createCommandId() };
      if (useDevelopmentFixtures) {
        await new Promise((resolve) => window.setTimeout(resolve, 280));
        applyDevelopmentCommand(command);
        return;
      }
      try { await submitOperationalCommand(request, user?.id ?? "anonymous"); await refresh(); }
      finally { setPendingCommands(pendingOperationalCommands(user?.id ?? "anonymous")); }
    },
    [applyDevelopmentCommand, refresh, user?.id],
  );

  const value = useMemo<DataContextValue>(
    () => ({ snapshot, status, error, connection, refresh, executeCommand, pendingCommands }),
    [connection, error, executeCommand, refresh, snapshot, status, pendingCommands],
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useErpData(): DataContextValue {
  const value = useContext(DataContext);
  if (!value) throw new Error("useErpData debe utilizarse dentro de DataProvider.");
  return value;
}
